import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
    authEmailLookup,
    contact,
    employeeLookup,
    employeePasswordSignin,
    hrContactRequest,
    nominateEmployer,
    passwordSetupEmail,
} from "../lib/api";
import { track } from "../lib/analytics";
import { isValidEmail, titleCaseName } from "../lib/dataHygiene";
import BackLink from "../components/BackLink";
import WrongDoorRedirect from "../components/WrongDoorRedirect";

import "./Apply.css";
import "./SignIn.css";

/**
 * Phase 1.5d — /apply discovery + recovery hub.
 *
 * A single page with ~7 surfaces driven by client state and an email
 * lookup outcome. Retires the old Phase 2a linear /apply state machine.
 */
const LOOKUP_STEPS = [
    "Checking the invitation list…",
    "Looking for your employer…",
    "Matching your email domain…",
];

export default function Apply() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [params] = useSearchParams();

    // Surface state machine — see spec §Part 1.
    //   "lookup"        — form (default)
    //   "looking_up"    — computing animation
    //   "exact_match"   — employer set you up; link resent
    //   "domain_match"  — employer is enrolled, email isn't on list
    //   "no_match"      — domain isn't in our records
    //   "hr_contact"    — HR outreach form + confirmation
    //   "nominate"      — employer nomination form + confirmation
    //   "contact_us"    — contact-us escape hatch + confirmation
    //   "err"           — generic lookup failure (rare)
    const [surface, setSurface] = useState("lookup");
    const [email, setEmail] = useState("");
    const [outcome, setOutcome] = useState(null);
    const [lookupStep, setLookupStep] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState(null);

    // Phase A2 — password form state for the `exact_match` surface.
    // The old magic-link /sent variant has been retired (magic links
    // still arrive in the inbox as a fallback until A6 cleanup).
    const [signinPassword, setSigninPassword] = useState("");
    const [signinRemember, setSigninRemember] = useState(false);
    const [signinSubmitting, setSigninSubmitting] = useState(false);
    // One of "" | "invalid" | "locked" | "password_not_set" | "network"
    const [signinError, setSigninError] = useState("");
    const [signinLockSeconds, setSigninLockSeconds] = useState(0);

    useEffect(() => {
        document.title = t("apply.hub.title");
        track("apply_hub_view", {});
        // Pre-fill email from query string (?email=) for recovery links.
        const preset = params.get("email");
        if (preset) setEmail(preset);
    }, [t, params]);

    // Looking-up computing animation
    useEffect(() => {
        if (surface !== "looking_up") return undefined;
        setLookupStep(0);
        const timers = [];
        for (let i = 0; i < LOOKUP_STEPS.length; i += 1) {
            timers.push(setTimeout(() => setLookupStep((s) => Math.max(s, i + 1)), 400 + i * 450));
        }
        return () => timers.forEach(clearTimeout);
    }, [surface]);

    // Phase 3e mobile fix — when the surface changes (e.g. lookup → no_match
    // → set_password), reset scroll to top and blur any focused input so
    // the iOS keyboard dismisses. Without this, the page stayed scrolled
    // to wherever the keyboard had shoved it, cutting off the title and
    // CTAs of the new surface.
    useEffect(() => {
        if (surface === "lookup") return; // preserve scroll on the initial surface
        try {
            if (document.activeElement && typeof document.activeElement.blur === "function") {
                document.activeElement.blur();
            }
        } catch (_e) { /* no-op */ }
        // rAF to let the new surface paint first, then scroll.
        requestAnimationFrame(() => {
            window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        });
    }, [surface]);

    // Phase A2 — BroadcastChannel + polling on `exact_match` are retired.
    // That surface now shows a password form; magic-link-based unlock is
    // no longer the primary auth path. The old listener was what made
    // the old "Check your inbox" UI unlock automatically when the user
    // clicked the magic link in another tab. With password sign-in, the
    // user stays on this tab, types their password, and navigates
    // themselves.

    const goLookup = () => {
        setErr(null);
        setOutcome(null);
        setSurface("lookup");
        // Fresh exact_match visits should start with clean signin state.
        setSigninPassword("");
        setSigninRemember(false);
        setSigninError("");
        setSigninLockSeconds(0);
    };

    const runLookup = async (submittedEmail) => {
        const trimmed = (submittedEmail || email).trim();
        if (!trimmed || !isValidEmail(trimmed)) {
            setErr(t("auth.common.emailInvalid"));
            return;
        }
        setErr(null);
        setSurface("looking_up");
        track("apply_hub_lookup_submit", {});
        // Hold on "looking_up" for at least ~1.2s so the animation
        // completes even on fast backends.
        const started = Date.now();
        try {
            // Fan out the cheap audience-agnostic lookup in parallel so we
            // can detect a pure-employer account and route them to the
            // right front door (Phase 3 unified entry).
            const [res, authRes] = await Promise.all([
                employeeLookup(trimmed),
                authEmailLookup({ email: trimmed, context: "employee" })
                    .catch(() => null),
            ]);
            const elapsed = Date.now() - started;
            const hold = Math.max(0, 1400 - elapsed);
            setTimeout(() => {
                // Pure employer admin → redirect to /employer-signin.
                if (
                    authRes &&
                    authRes.account_type === "employer" &&
                    res.outcome !== "exact_match"
                ) {
                    setOutcome({ email: trimmed });
                    setSurface("wrong_door_employer");
                    track("apply_hub_wrong_door", { direction: "toEmployer" });
                    return;
                }
                setOutcome(res);
                setSurface(res.outcome);
                track(`apply_hub_outcome_${res.outcome}`, { resendStatus: res.resend_status });
            }, hold);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            setErr(
                typeof detail === "object" ? detail?.message || t("apply.hub.err.generic")
                    : (detail || t("apply.hub.err.generic")),
            );
            setSurface("lookup");
        }
    };

    // Phase A2 — onResendAgain retired. The old exact_match surface had
    // a "send again" button tied to the magic-link flow; the password
    // form replaces the whole interaction. Magic links still arrive as a
    // fallback until A6 cleanup, so no resend UI is needed here.

    /* ----- Render helpers ----- */
    const renderLookup = () => (
        <div className="apply-surface" data-testid="apply-surface-lookup">
            <BackLink
                to="/employees"
                testId="apply-lookup-back"
                label={t("auth.common.back")}
            />
            <div className="surface-label">{t("apply.hub.lookup.label")}</div>
            <h1 className="surface-title">
                {t("apply.hub.lookup.h1a")} <em>{t("apply.hub.lookup.h1b")}</em>
            </h1>
            <p className="surface-lede">{t("apply.hub.lookup.lede")}</p>

            {/* Phase A6 — legacy magic-link arrivals land here via the
                /team/verify and /employee/verify redirects. Non-blocking
                info banner so the user understands why they're here. */}
            {params.get("reason") === "link_expired" && (
                <div
                    className="surface-err"
                    data-testid="apply-lookup-link-expired"
                    role="status"
                    style={{ background: "var(--accent-soft)", borderLeftColor: "var(--accent)", color: "var(--ink)" }}
                >
                    {t("auth.legacy.linkExpired")}
                </div>
            )}

            {err && <div className="surface-err" data-testid="apply-lookup-err">{err}</div>}

            <form
                className="surface-form"
                onSubmit={(e) => { e.preventDefault(); runLookup(); }}
            >
                <label className="surface-label-inline">{t("apply.hub.lookup.emailLabel")}</label>
                <div className="lookup-row">
                    <input
                        type="email"
                        className="surface-input"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onBlur={(e) => setEmail(e.target.value.trim())}
                        placeholder={t("apply.hub.lookup.emailPlaceholder")}
                        autoFocus
                        required
                        data-testid="apply-lookup-input"
                    />
                    <button
                        type="submit"
                        className="surface-btn surface-btn-primary"
                        data-testid="apply-lookup-submit"
                    >
                        {t("apply.hub.lookup.cta")}
                    </button>
                </div>
            </form>

            <div style={{ marginTop: "3rem", display: "flex", flexDirection: "column", gap: "1.25rem", alignItems: "flex-start" }}>
                <Link
                    to="/employer-signin"
                    className="signin-audience-swap"
                    data-testid="apply-lookup-switch-employer"
                    onClick={() => track("apply_hub_switch_to_employer", {})}
                >
                    {t("apply.hub.lookup.switchToEmployer")}
                </Link>
                <button
                    type="button"
                    className="surface-link"
                    onClick={() => setSurface("contact_us")}
                    data-testid="apply-lookup-contact-us"
                >
                    {t("apply.hub.lookup.contactUs")} ↗︎
                </button>
            </div>
        </div>
    );

    const renderLookingUp = () => (
        <div className="apply-surface" data-testid="apply-surface-looking_up">
            <div className="surface-label">{t("apply.hub.lookingUp.label")}</div>
            <h1 className="surface-title">{t("apply.hub.lookingUp.h1")}</h1>
            <p className="surface-lede">{t("apply.hub.lookingUp.lede")}</p>
            <div className="looking-up">
                <div className="looking-up-steps">
                    {LOOKUP_STEPS.map((s, i) => (
                        <div
                            key={i}
                            className={`looking-up-step ${i < lookupStep ? "visible done" : i === lookupStep ? "visible" : ""}`}
                        >
                            <span className="looking-up-dot" />
                            <span>{t(`apply.hub.lookingUp.steps.${i}`, s)}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    const handleExactMatchSignin = async (e) => {
        e.preventDefault();
        if (signinSubmitting || signinPassword.length < 1) return;
        setSigninError("");
        setSigninSubmitting(true);
        try {
            const res = await employeePasswordSignin({
                email: outcome?.email || email,
                password: signinPassword,
                rememberMe: signinRemember,
            });
            track("signin_submit", {
                audience: "employee",
                channel: "password",
                remember_me: signinRemember,
                outcome: "ok",
            });
            const dest = res?.redirect_to || "/portal";
            window.location.replace(dest);
        } catch (error) {
            const status = error?.response?.status;
            const detail = error?.response?.data?.detail;
            if (status === 429 && detail === "locked_out") {
                setSigninError("locked");
                setSigninLockSeconds(
                    error.response.data?.retry_after_seconds || 15 * 60,
                );
                track("signin_submit", { audience: "employee", outcome: "locked_out" });
            } else if (status === 401 && detail === "password_not_set") {
                setSigninError("password_not_set");
                // Phase A4 — fire a setup-email so they can create their
                // password. Enumeration-safe; server returns 200 regardless.
                try {
                    await passwordSetupEmail({
                        role: "employee",
                        email: (outcome?.email || email).trim(),
                        locale: i18n?.language?.startsWith("es") ? "es" : "en",
                    });
                    track("password_setup_email_sent", { audience: "employee" });
                } catch (_) {
                    // Swallow — we still show the "check your email" copy.
                }
                track("signin_submit", {
                    audience: "employee", outcome: "password_not_set",
                });
            } else if (status === 401) {
                setSigninError("invalid");
                track("signin_submit", {
                    audience: "employee", outcome: "invalid_credentials",
                });
            } else {
                setSigninError("network");
                track("signin_submit", { audience: "employee", outcome: "error" });
            }
        } finally {
            setSigninSubmitting(false);
        }
    };

    const renderExactMatch = () => {
        const errorCopyKey = {
            invalid: "auth.signin.errors.invalidCredentials",
            locked: "auth.signin.errors.lockedOut",
            password_not_set: "auth.signin.errors.passwordNotSet",
            network: "auth.signin.errors.network",
        }[signinError];
        const recognizedEmail = outcome?.email || email;
        const firstName = outcome?.first_name || "";

        return (
            <div className="apply-surface" data-testid="apply-surface-exact_match">
                <BackLink
                    onClick={goLookup}
                    testId="apply-exact-back"
                    label={t("auth.common.startOver")}
                />
                <div className="surface-label">{t("apply.hub.exact.label")}</div>
                <h1 className="surface-title">
                    {firstName
                        ? t("apply.hub.exact.h1a", { firstName })
                        : t("auth.signin.welcomeBack")}
                </h1>

                <form
                    className="signin-form apply-exact-password"
                    onSubmit={handleExactMatchSignin}
                    autoComplete="on"
                    data-testid="apply-exact-form"
                    noValidate
                >
                    <div className="apply-exact-email-row">
                        <span className="mono">{t("auth.signin.emailLabel")}:</span>
                        <span
                            className="apply-exact-email-value"
                            data-testid="apply-exact-email"
                        >
                            {recognizedEmail}
                        </span>
                        <button
                            type="button"
                            className="apply-exact-not-you"
                            onClick={goLookup}
                            data-testid="apply-exact-not-you"
                        >
                            {t("auth.signin.notYou")}
                        </button>
                    </div>

                    <div
                        className="signin-error-live"
                        aria-live="polite"
                        data-testid="apply-exact-error"
                    >
                        {errorCopyKey && (
                            <div className="signin-error">
                                {t(errorCopyKey, {
                                    minutes: Math.ceil((signinLockSeconds || 900) / 60),
                                })}
                            </div>
                        )}
                    </div>

                    <label className="signin-label mono" htmlFor="apply-exact-password">
                        {t("auth.signin.passwordLabel")}
                    </label>
                    <input
                        id="apply-exact-password"
                        type="password"
                        name="password"
                        autoComplete="current-password"
                        autoFocus
                        required
                        value={signinPassword}
                        onChange={(ev) => setSigninPassword(ev.target.value)}
                        className="signin-input"
                        data-testid="apply-exact-password"
                    />

                    <label className="signin-remember">
                        <input
                            type="checkbox"
                            checked={signinRemember}
                            onChange={(ev) => setSigninRemember(ev.target.checked)}
                            data-testid="apply-exact-remember-me"
                        />
                        <span>{t("auth.signin.rememberLabel")}</span>
                    </label>

                    <button
                        type="submit"
                        className="signin-submit"
                        disabled={signinPassword.length < 1 || signinSubmitting}
                        data-testid="apply-exact-submit"
                    >
                        {signinSubmitting
                            ? t("auth.signin.submitting")
                            : t("auth.signin.submit")}
                    </button>

                    <div className="signin-ancillary">
                        <Link
                            to={`/forgot-password?role=employee&email=${encodeURIComponent(recognizedEmail)}`}
                            className="signin-forgot"
                            data-testid="apply-exact-forgot"
                        >
                            {t("auth.signin.forgot")}
                        </Link>
                    </div>
                </form>
            </div>
        );
    };

    const renderDomainMatch = () => (
        <div className="apply-surface" data-testid="apply-surface-domain_match">
            <BackLink
                onClick={goLookup}
                testId="apply-domain-back"
                label={t("auth.common.startOver")}
            />
            <div className="surface-label">{t("apply.hub.domain.label")}</div>
            <h1 className="surface-title">
                {t("apply.hub.domain.h1a", { employer: outcome?.employer_name || "" })}{" "}
                <em>{t("apply.hub.domain.h1b")}</em>
            </h1>
            <p className="surface-lede">
                {t("apply.hub.domain.lede", { email: outcome?.email || email })}
            </p>

            <div className="surface-form" style={{ gap: "0.75rem" }}>
                <button
                    type="button"
                    className="option-card"
                    onClick={goLookup}
                    data-testid="domain-option-try-different"
                >
                    <div>
                        <div className="option-title">{t("apply.hub.domain.option1.title")}</div>
                        <div className="option-desc">{t("apply.hub.domain.option1.desc")}</div>
                    </div>
                    <span className="option-arrow">↗︎</span>
                </button>
                <button
                    type="button"
                    className="option-card"
                    onClick={() => setSurface("hr_contact")}
                    data-testid="domain-option-hr-contact"
                >
                    <div>
                        <div className="option-title">{t("apply.hub.domain.option2.title")}</div>
                        <div className="option-desc">{t("apply.hub.domain.option2.desc")}</div>
                    </div>
                    <span className="option-arrow">↗︎</span>
                </button>
                <button
                    type="button"
                    className="option-card"
                    onClick={() => setSurface("contact_us")}
                    data-testid="domain-option-human"
                >
                    <div>
                        <div className="option-title">{t("apply.hub.domain.option3.title")}</div>
                        <div className="option-desc">{t("apply.hub.domain.option3.desc")}</div>
                    </div>
                    <span className="option-arrow">↗︎</span>
                </button>
            </div>

            <button className="surface-back" onClick={goLookup}>
                ←︎ {t("apply.hub.back")}
            </button>
        </div>
    );

    const renderNoMatch = () => (
        <div className="apply-surface" data-testid="apply-surface-no_match">
            <BackLink
                onClick={goLookup}
                testId="apply-nomatch-back"
                label={t("auth.common.startOver")}
            />
            <div className="surface-label">{t("apply.hub.noMatch.label")}</div>
            <h1 className="surface-title">
                {t("apply.hub.noMatch.h1a", { domain: outcome?.domain || "" })}{" "}
                <em>{t("apply.hub.noMatch.h1b")}</em>
            </h1>
            <p className="surface-lede">{t("apply.hub.noMatch.lede")}</p>

            <div className="surface-form" style={{ gap: "0.75rem" }}>
                <button
                    type="button"
                    className="option-card"
                    onClick={goLookup}
                    data-testid="nomatch-option-work-email"
                >
                    <div>
                        <div className="option-title">{t("apply.hub.noMatch.option1.title")}</div>
                        <div className="option-desc">{t("apply.hub.noMatch.option1.desc")}</div>
                    </div>
                    <span className="option-arrow">↗︎</span>
                </button>
                <button
                    type="button"
                    className="option-card"
                    onClick={() => setSurface("nominate")}
                    data-testid="nomatch-option-nominate"
                >
                    <div>
                        <div className="option-title">{t("apply.hub.noMatch.option2.title")}</div>
                        <div className="option-desc">{t("apply.hub.noMatch.option2.desc")}</div>
                    </div>
                    <span className="option-arrow">↗︎</span>
                </button>
            </div>

            <button className="surface-back" onClick={goLookup}>
                ←︎ {t("apply.hub.back")}
            </button>
        </div>
    );

    const renderHRContact = () => (
        <HRContactForm
            employerId={outcome?.employer_id}
            employerName={outcome?.employer_name}
            employeeEmail={outcome?.email || email}
            onCancel={() => setSurface("domain_match")}
        />
    );

    const renderNominate = () => (
        <NominateForm
            employeeEmail={outcome?.email || email}
            domain={outcome?.domain}
            onCancel={() => setSurface("no_match")}
        />
    );

    const renderContactUs = () => (
        <ContactUsForm
            employeeEmail={outcome?.email || email}
            onCancel={() => setSurface(outcome?.outcome || "lookup")}
        />
    );

    return (
        <section className="apply-hub-page" data-testid="apply-hub-page">
            <div className="apply-hub-wrap">
                {surface === "lookup" && renderLookup()}
                {surface === "looking_up" && renderLookingUp()}
                {surface === "exact_match" && renderExactMatch()}
                {surface === "domain_match" && renderDomainMatch()}
                {surface === "no_match" && renderNoMatch()}
                {surface === "hr_contact" && renderHRContact()}
                {surface === "nominate" && renderNominate()}
                {surface === "contact_us" && renderContactUs()}
                {surface === "wrong_door_employer" && (
                    <WrongDoorRedirect
                        to="/employer-signin"
                        email={outcome?.email || email}
                        direction="toEmployer"
                        onCancel={goLookup}
                        testId="apply-wrong-door"
                    />
                )}
            </div>
        </section>
    );
}


/* ----- Sub-forms ----- */
function HRContactForm({ employerId, employerName, employeeEmail, onCancel }) {
    const { t } = useTranslation();
    const [hrName, setHrName] = useState("");
    const [hrEmail, setHrEmail] = useState("");
    const [message, setMessage] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);
    const [err, setErr] = useState(null);
    const [fieldErr, setFieldErr] = useState({});

    const submit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        const fe = {};
        if (!hrName.trim()) fe.hrName = t("errors.required") || "Required";
        if (!isValidEmail(hrEmail.trim())) fe.hrEmail = t("auth.common.emailInvalid");
        setFieldErr(fe);
        if (Object.keys(fe).length > 0) return;
        setSubmitting(true);
        setErr(null);
        try {
            await hrContactRequest({
                employer_id: employerId,
                employee_email: employeeEmail,
                hr_name: hrName.trim(),
                hr_email: hrEmail.trim(),
                message: message.trim() || undefined,
            });
            setSent(true);
            track("apply_hub_hr_contact_submitted", {});
        } catch (e2) {
            setErr(e2?.response?.data?.detail || t("apply.hub.err.generic"));
        } finally {
            setSubmitting(false);
        }
    };

    if (sent) {
        return (
            <div className="apply-surface" data-testid="apply-surface-hr-sent">
                <BackLink
                    onClick={onCancel}
                    testId="hr-sent-back"
                    label={t("auth.common.backToOptions")}
                />
                <div className="surface-label">{t("apply.hub.hrContact.sentLabel")}</div>
                <h1 className="surface-title">
                    {t("apply.hub.hrContact.sentH1a")} <em>{t("apply.hub.hrContact.sentH1b")}</em>
                </h1>
                <p className="surface-lede">
                    {t("apply.hub.hrContact.sentBody", { hrName, employeeEmail })}
                </p>
                <div className="surface-note">
                    <strong>{t("apply.hub.hrContact.nextStepLabel")}</strong>
                    <div style={{ marginTop: 6 }}>{t("apply.hub.hrContact.nextStepBody")}</div>
                </div>
            </div>
        );
    }

    return (
        <div className="apply-surface" data-testid="apply-surface-hr_contact">
            <BackLink
                onClick={onCancel}
                testId="hr-contact-back"
                label={t("auth.common.backToOptions")}
            />
            <div className="surface-label">{t("apply.hub.hrContact.label")}</div>
            <h1 className="surface-title">
                {t("apply.hub.hrContact.h1a")} <em>{t("apply.hub.hrContact.h1b")}</em>
            </h1>
            <p className="surface-lede">
                {t("apply.hub.hrContact.lede", { employer: employerName })}
            </p>
            {err && <div className="surface-err">{typeof err === "string" ? err : err?.message}</div>}

            <form className="surface-form" onSubmit={submit}>
                <div>
                    <span className="surface-label-inline">{t("apply.hub.hrContact.hrNameLabel")}</span>
                    <input
                        type="text"
                        className="surface-input"
                        value={hrName}
                        onChange={(e) => setHrName(e.target.value)}
                        onBlur={(e) => setHrName(titleCaseName(e.target.value))}
                        required
                        data-testid="hr-name-input"
                    />
                    {fieldErr.hrName && (
                        <div className="surface-err" data-testid="hr-name-err">{fieldErr.hrName}</div>
                    )}
                </div>
                <div>
                    <span className="surface-label-inline">{t("apply.hub.hrContact.hrEmailLabel")}</span>
                    <input
                        type="email"
                        className="surface-input"
                        value={hrEmail}
                        onChange={(e) => setHrEmail(e.target.value)}
                        onBlur={(e) => setHrEmail(e.target.value.trim())}
                        required
                        data-testid="hr-email-input"
                    />
                    {fieldErr.hrEmail && (
                        <div className="surface-err" data-testid="hr-email-err">{fieldErr.hrEmail}</div>
                    )}
                </div>
                <div>
                    <span className="surface-label-inline">{t("apply.hub.hrContact.messageLabel")}</span>
                    <textarea
                        className="surface-textarea"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        maxLength={1000}
                        placeholder={t("apply.hub.hrContact.messagePlaceholder")}
                        data-testid="hr-message-input"
                    />
                </div>
                <div className="surface-actions-row">
                    <button
                        type="submit"
                        className="surface-btn surface-btn-primary"
                        disabled={submitting}
                        data-testid="hr-submit"
                    >
                        {submitting ? t("apply.hub.hrContact.submitting") : t("apply.hub.hrContact.submit")}
                    </button>
                    <button
                        type="button"
                        className="surface-btn-ghost"
                        onClick={onCancel}
                    >
                        {t("apply.hub.back")}
                    </button>
                </div>
            </form>
        </div>
    );
}


function NominateForm({ employeeEmail, domain, onCancel }) {
    const { t } = useTranslation();
    const [companyName, setCompanyName] = useState("");
    const [role, setRole] = useState("");
    const [email, setEmail] = useState(employeeEmail || "");
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);
    const [err, setErr] = useState(null);
    const [fieldErr, setFieldErr] = useState({});

    const submit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        const fe = {};
        if (!companyName.trim()) fe.companyName = t("errors.required") || "Required";
        if (!isValidEmail(email.trim())) fe.email = t("auth.common.emailInvalid");
        setFieldErr(fe);
        if (Object.keys(fe).length > 0) return;
        setSubmitting(true);
        setErr(null);
        try {
            await nominateEmployer({
                submitter_email: email.trim(),
                company_name: companyName.trim(),
                role: role.trim() || undefined,
                source: "apply_no_match",
                domain,
            });
            setSent(true);
            track("apply_hub_nominated", {});
        } catch (e2) {
            setErr(e2?.response?.data?.detail || t("apply.hub.err.generic"));
        } finally {
            setSubmitting(false);
        }
    };

    if (sent) {
        return (
            <div className="apply-surface" data-testid="apply-surface-nominate-sent">
                <BackLink
                    onClick={onCancel}
                    testId="nominate-sent-back"
                    label={t("auth.common.backToOptions")}
                />
                <div className="surface-label">{t("apply.hub.nominate.sentLabel")}</div>
                <h1 className="surface-title">
                    {t("apply.hub.nominate.sentH1a")} <em>{t("apply.hub.nominate.sentH1b")}</em>
                </h1>
                <p className="surface-lede">{t("apply.hub.nominate.sentBody")}</p>
            </div>
        );
    }

    return (
        <div className="apply-surface" data-testid="apply-surface-nominate">
            <BackLink
                onClick={onCancel}
                testId="nominate-back"
                label={t("auth.common.backToOptions")}
            />
            <div className="surface-label">{t("apply.hub.nominate.label")}</div>
            <h1 className="surface-title">
                {t("apply.hub.nominate.h1a")} <em>{t("apply.hub.nominate.h1b")}</em>
            </h1>
            <p className="surface-lede">{t("apply.hub.nominate.lede")}</p>
            {err && <div className="surface-err">{typeof err === "string" ? err : err?.message}</div>}

            <form className="surface-form" onSubmit={submit}>
                <div>
                    <span className="surface-label-inline">{t("apply.hub.nominate.companyLabel")}</span>
                    <input
                        type="text"
                        className="surface-input"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        required
                        data-testid="nominate-company-input"
                    />
                </div>
                <div>
                    <span className="surface-label-inline">{t("apply.hub.nominate.roleLabel")}</span>
                    <input
                        type="text"
                        className="surface-input"
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                        placeholder={t("apply.hub.nominate.rolePlaceholder")}
                        data-testid="nominate-role-input"
                    />
                </div>
                <div>
                    <span className="surface-label-inline">{t("apply.hub.nominate.emailLabel")}</span>
                    <input
                        type="email"
                        className="surface-input"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onBlur={(e) => setEmail(e.target.value.trim())}
                        required
                        data-testid="nominate-email-input"
                    />
                    {fieldErr.email && (
                        <div className="surface-err" data-testid="nominate-email-err">{fieldErr.email}</div>
                    )}
                </div>
                <div className="surface-actions-row">
                    <button
                        type="submit"
                        className="surface-btn surface-btn-primary"
                        disabled={submitting}
                        data-testid="nominate-submit"
                    >
                        {submitting ? t("apply.hub.nominate.submitting") : t("apply.hub.nominate.submit")}
                    </button>
                    <button
                        type="button"
                        className="surface-btn-ghost"
                        onClick={onCancel}
                    >
                        {t("apply.hub.back")}
                    </button>
                </div>
            </form>
        </div>
    );
}


function ContactUsForm({ employeeEmail, onCancel }) {
    const { t } = useTranslation();
    const [name, setName] = useState("");
    const [email, setEmail] = useState(employeeEmail || "");
    const [message, setMessage] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);
    const [err, setErr] = useState(null);
    const [fieldErr, setFieldErr] = useState({});

    const submit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        const fe = {};
        if (!name.trim()) fe.name = t("errors.required") || "Required";
        if (!isValidEmail(email.trim())) fe.email = t("auth.common.emailInvalid");
        if (!message.trim()) fe.message = t("errors.required") || "Required";
        setFieldErr(fe);
        if (Object.keys(fe).length > 0) return;
        setSubmitting(true);
        setErr(null);
        try {
            await contact({
                name: name.trim(),
                email: email.trim(),
                phone: "",
                message: message.trim(),
                source: "apply_escape_hatch",
            });
            setSent(true);
            track("apply_hub_contact_submitted", {});
        } catch (e2) {
            setErr(e2?.response?.data?.detail || t("apply.hub.err.generic"));
        } finally {
            setSubmitting(false);
        }
    };

    if (sent) {
        return (
            <div className="apply-surface" data-testid="apply-surface-contact-sent">
                <BackLink
                    onClick={onCancel}
                    testId="contact-sent-back"
                    label={t("auth.common.backToOptions")}
                />
                <div className="surface-label">{t("apply.hub.contactUs.sentLabel")}</div>
                <h1 className="surface-title">
                    {t("apply.hub.contactUs.sentH1a")} <em>{t("apply.hub.contactUs.sentH1b")}</em>
                </h1>
                <p className="surface-lede">{t("apply.hub.contactUs.sentBody")}</p>
            </div>
        );
    }

    return (
        <div className="apply-surface" data-testid="apply-surface-contact_us">
            <BackLink
                onClick={onCancel}
                testId="contact-us-back"
                label={t("auth.common.back")}
            />
            <div className="surface-label">{t("apply.hub.contactUs.label")}</div>
            <h1 className="surface-title">
                {t("apply.hub.contactUs.h1a")} <em>{t("apply.hub.contactUs.h1b")}</em>
            </h1>
            <p className="surface-lede">{t("apply.hub.contactUs.lede")}</p>
            {err && <div className="surface-err">{typeof err === "string" ? err : err?.message}</div>}

            <form className="surface-form" onSubmit={submit}>
                <div>
                    <span className="surface-label-inline">{t("apply.hub.contactUs.nameLabel")}</span>
                    <input
                        type="text"
                        className="surface-input"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onBlur={(e) => setName(titleCaseName(e.target.value))}
                        required
                        data-testid="contact-name-input"
                    />
                    {fieldErr.name && (
                        <div className="surface-err" data-testid="contact-name-err">{fieldErr.name}</div>
                    )}
                </div>
                <div>
                    <span className="surface-label-inline">{t("apply.hub.contactUs.emailLabel")}</span>
                    <input
                        type="email"
                        className="surface-input"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onBlur={(e) => setEmail(e.target.value.trim())}
                        required
                        data-testid="contact-email-input"
                    />
                    {fieldErr.email && (
                        <div className="surface-err" data-testid="contact-email-err">{fieldErr.email}</div>
                    )}
                </div>
                <div>
                    <span className="surface-label-inline">{t("apply.hub.contactUs.messageLabel")}</span>
                    <textarea
                        className="surface-textarea"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        maxLength={1000}
                        required
                        placeholder={t("apply.hub.contactUs.messagePlaceholder")}
                        data-testid="contact-message-input"
                    />
                </div>
                <div className="surface-actions-row">
                    <button
                        type="submit"
                        className="surface-btn surface-btn-primary"
                        disabled={submitting}
                        data-testid="contact-submit"
                    >
                        {submitting ? t("apply.hub.contactUs.submitting") : t("apply.hub.contactUs.submit")}
                    </button>
                    <button
                        type="button"
                        className="surface-btn-ghost"
                        onClick={onCancel}
                    >
                        {t("apply.hub.back")}
                    </button>
                </div>
            </form>
        </div>
    );
}
